import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { detailFromResponse, getSessionUser, listFromResponse, normalizeMasterOptions, readBase64, type Option } from "./helpers";

/** Session user, filled from employees/my-profile when family members are missing. */
export async function loadEmployeeUser() {
  const session = getSessionUser();
  try {
    const res = await postApi(API_URLS.EMPLOYEES.My_PROFILE, {});
    const body = res?.data?.user || res?.data?.data || res?.data || res?.user || {};
    const user = body?.details || body?.familyMembers ? body : body?.user || body;
    if (user && (user.details || user.familyMembers)) {
      return {
        ...session,
        ...user,
        details: { ...(session?.details || {}), ...(user.details || {}) },
        familyMembers: user.familyMembers || session?.familyMembers || [],
      };
    }
  } catch {
    /* keep the logged-in session */
  }
  return session;
}

const DF = API_URLS.DEPENDENT_FAMILY;

function messageFromError(error: any): string {
  const body = error?.response?.data;
  const nested = body?.data;
  return (
    (typeof nested?.message === "string" && nested.message) ||
    (typeof body?.message === "string" && body.message) ||
    (typeof error?.message === "string" && error.message) ||
    "Something went wrong"
  );
}

/** Same shape as the mobile apiCall result: HTTP 422 still returns success/message. */
export async function postApi(url: string, payload: any) {
  try {
    const response = await Api.post(url, payload);
    return response?.data ?? response ?? {};
  } catch (error: any) {
    const body = error?.response?.data;
    const nested = body?.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : {};
    return {
      ...nested,
      success: false,
      status: nested?.status || body?.status || error?.response?.status,
      message: messageFromError(error),
    };
  }
}

export async function fetchOptions(url: string): Promise<Option[]> {
  const res = await postApi(url, {});
  const list = normalizeMasterOptions(res?.data?.data ?? res?.data ?? []);
  return res?.success && list.length > 0 ? list : [];
}

export const masters = {
  additionOf: () => fetchOptions(DF.GET_ADDITION_OF_OPTIONS),
  occupation: () => fetchOptions(DF.GET_OCCUPATION_OPTIONS),
  siblingOccupation: () => fetchOptions(DF.GET_SIBLING_OCCUPATION_OPTIONS),
  relationship: () => fetchOptions(DF.FAMILY_RELATIONSHIP),
  gender: () => fetchOptions(DF.FAMILY_GENDER),
  nationality: () => fetchOptions(DF.FAMILY_NATIONALITY),
  dependencyType: () => fetchOptions(DF.FAMILY_DEPENDENCY_TYPE),
  employment: () => fetchOptions(DF.FAMILY_EMP_STATUS),
  roof: () => fetchOptions(DF.FAMILY_ROOF),
  reasonRoof: () => fetchOptions(DF.FAMILY_REASON_ROOF),
  marital: () => fetchOptions(DF.FAMILY_MARITAL),
};

export async function findAllInclusion(empId: string) {
  const res = await postApi(DF.FIND_ALL_INCLUSION, { empId });
  return { ok: !!res?.success, message: res?.message, list: listFromResponse(res) };
}

export async function syncInclusion() {
  return postApi(DF.SYNC_INCLUSION, {});
}

export async function createInclusion(payload: any) {
  return postApi(DF.CREATE_INCLUSION, payload);
}

export async function findOneInclusion(referenceNumber: string) {
  const res = await postApi(DF.FIND_ONE_INCLUSION, { referenceNumber });
  return { ok: !!res?.success, message: res?.message, data: detailFromResponse(res) };
}

export async function approveInclusion(payload: any) {
  return postApi(DF.APPROVE_INCLUSION, payload);
}

export async function findAllDeletion(empId: string) {
  const res = await postApi(DF.DELETION_FIND_ALL, { empId });
  return { ok: !!res?.success, message: res?.message, list: listFromResponse(res) };
}

export async function createDeletion(payload: any) {
  return postApi(DF.DELETION_CREATE, payload);
}

export async function findOneDeletion(referenceNumber: string) {
  const res = await postApi(DF.DELETION_FIND_ONE, { referenceNumber });
  return { ok: !!res?.success, message: res?.message, data: detailFromResponse(res) };
}

export async function approveDeletion(payload: any) {
  return postApi(DF.DELETION_APPROVE, payload);
}

export async function findAllFamily(empId: string) {
  const res = await postApi(DF.FAMILY_FIND_ALL, { empId });
  return { ok: !!res?.success, message: res?.message, list: listFromResponse(res) };
}

export async function syncFamily(empId: string) {
  return postApi(DF.FAMILY_SYNC, { empId });
}

export async function createFamily(payload: any) {
  return postApi(DF.FAMILY_CREATE, payload);
}

export async function deleteFamily(payload: any) {
  return postApi(DF.DELETION_CREATE, payload);
}

export async function findOneFamily(referenceNumber: string) {
  const res = await postApi(DF.FAMILY_FIND_ONE, { referenceNumber });
  return { ok: !!res?.success, message: res?.message, data: detailFromResponse(res) };
}

export async function approveFamily(payload: any) {
  return postApi(DF.FAMILY_APPROVE, payload);
}

export async function getEmployeeFamily(empId: string) {
  const res = await postApi(DF.FAMILY_GET_EMPLOYEE, { empId });
  const body = res?.data?.data ?? res?.data ?? {};
  const fam = Array.isArray(body) ? body : body?.ToFamily ?? body?.toFamily ?? [];
  const list = Array.isArray(fam) ? fam : fam?.results ?? [];
  return { ok: !!res?.success, message: res?.message, list: Array.isArray(list) ? list : [] };
}

export async function uploadToModule(file: File, module: string, mimeType?: string, extra?: Record<string, any>) {
  const filestring = await readBase64(file);
  const res = await postApi(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
    filestring,
    mime_type: mimeType || file.type || "application/octet-stream",
    module,
    ...(extra || {}),
  });
  const key = res?.data?.key ?? res?.data?.data?.key;
  return { ok: !!(res?.success && key), message: res?.message, key: key ? String(key) : "" };
}

export async function openStoredFile(doc: any) {
  const key = doc?.key || (typeof doc?.Content === "string" && doc.Content.length > 0 && doc.Content.length < 300 ? doc.Content : "");
  let url: string = doc?.url || "";
  if (!url && key) {
    const res = await postApi(API_URLS.AWS_BUCKET.GET_PRESIGNED_URL, { key });
    const d = res?.data ?? res;
    url =
      (typeof d === "string" ? d : "") ||
      d?.url ||
      d?.presigned_url ||
      (typeof d?.data === "string" ? d.data : "") ||
      d?.data?.url ||
      "";
  }
  if (!url) throw new Error("Could not open the document");
  window.open(url, "_blank", "noopener,noreferrer");
}
